/**
 * Pure HEXA rollup math (REV20 — docs/specs/REV20-hexa-page.md), extracted from
 * `components/goals/HexaSection.tsx` (the REV9 per-character node editor) so `components/hexa/
 * HexaOverview.tsx` (the new account-wide summary) can reuse the exact same numbers rather than
 * reimplementing them — the two surfaces must never drift out of sync. No React, no fetch: this
 * module only ever transforms an already-fetched `HexaNodeDto[]`.
 */
import type { ClassDto } from './reference';
import type { HexaNodeDto, HexaStatNodeDto } from './characterProgress';

export interface HexaRollup {
  totalSolErda: number;
  usedSolErda: number;
  totalFragments: number;
  usedFragments: number;
  missingSolErda: number;
  missingFragments: number;
  /** Fragment-based, rounded — matches `HexaSection`'s existing `overallPct` (0 when `totalFragments` is 0). */
  overallPct: number;
  /** REV22 Fix 2b (docs/specs/REV22-hexa-stat-and-merged-masteries.md) — true when `statNodes` was
   *  passed and contributed any nonzero SE/fragment figure to this rollup (in practice: whenever
   *  `statNodes` is given at all, since even an untouched node's expected-value cost to its own
   *  default 20 goal is nonzero). Every figure above is still exact when this is false. */
  estimated: boolean;
}

/**
 * Sums a single character's HEXA node list (plus, since REV22 Fix 2b, its HEXA Stat matrix) into the
 * SE/fragment rollup shown in both `HexaSection`'s subtitle and `HexaOverview`'s per-character
 * row/account totals strip. Each node's own `totalSolErda`/`totalFragments` are already relative to
 * that node's `targetLevel` (see `characterProgress.ts`'s `HexaNodeDto`), so this rollup is
 * goal-based automatically — no separate "target" variant needed.
 *
 * `statNodes` is optional and, when given, folds in the HEXA Stat matrix per the addendum's cost
 * model: unlock cost is exact (`unlockSolErda`/`unlockFragments`, counted once a node is `unlocked`);
 * leveling cost is the DP's expected-value estimate (`estFragmentsToGoal`/`estFragmentsSpent`) —
 * unlike every other HEXA node type, which has a fixed per-level curve. A caller that omits
 * `statNodes` gets the exact pre-Fix-2b totals (`estimated` stays false) — e.g. any consumer that
 * hasn't been updated to fetch the stat matrix, or a context where only exact node-level totals make
 * sense.
 */
export function hexaRollup(nodes: HexaNodeDto[], statNodes?: HexaStatNodeDto[]): HexaRollup {
  const totalSolErda = nodes.reduce((sum, n) => sum + n.totalSolErda, 0);
  const usedSolErda = nodes.reduce((sum, n) => sum + n.usedSolErda, 0);
  const totalFragments = nodes.reduce((sum, n) => sum + n.totalFragments, 0);
  const usedFragments = nodes.reduce((sum, n) => sum + n.usedFragments, 0);

  let statSolErda = 0;
  let statSolErdaUsed = 0;
  let statFragments = 0;
  let statFragmentsUsed = 0;
  for (const stat of statNodes ?? []) {
    statSolErda += stat.unlockSolErda;
    statSolErdaUsed += stat.unlocked ? stat.unlockSolErda : 0;
    statFragments += stat.unlockFragments + stat.estFragmentsSpent + stat.estFragmentsToGoal;
    statFragmentsUsed += (stat.unlocked ? stat.unlockFragments : 0) + stat.estFragmentsSpent;
  }

  const grandTotalSolErda = totalSolErda + statSolErda;
  const grandUsedSolErda = usedSolErda + statSolErdaUsed;
  const grandTotalFragments = totalFragments + statFragments;
  const grandUsedFragments = usedFragments + statFragmentsUsed;

  return {
    totalSolErda: grandTotalSolErda,
    usedSolErda: grandUsedSolErda,
    totalFragments: grandTotalFragments,
    usedFragments: grandUsedFragments,
    missingSolErda: grandTotalSolErda - grandUsedSolErda,
    missingFragments: grandTotalFragments - grandUsedFragments,
    overallPct: grandTotalFragments > 0 ? Math.round((grandUsedFragments / grandTotalFragments) * 100) : 0,
    estimated: statSolErda > 0 || statFragments > 0,
  };
}

/** Same node-type ordering `HexaSection`'s own `NODE_TYPE_ORDER` uses — kept as a separate literal
 *  here (rather than importing from that component file) so this module stays free of any React
 *  component dependency. */
const NODE_TYPE_ORDER = ['origin', 'ascent', 'boost', 'mastery', 'common_core', 'common_boost'] as const;

/** Single-index node types (one physical node, no "how many added" concept) — their chip shows the
 *  node's level directly, e.g. "Origin 30". */
const SINGLE_NODE_TYPES = new Set<string>(['origin', 'ascent']);

/** Flexible-count node types (the player adds 0-4 instances) — their chip shows how many the player
 *  has added so far, e.g. "Mastery 2 nodes", since there's no fixed denominator to show progress
 *  against. Fixed-count multi-node types (boost, common_core) instead show a maxed-count fraction. */
const FLEXIBLE_NODE_TYPES = new Set<string>(['mastery', 'common_boost']);

const CHIP_TYPE_LABEL: Record<string, string> = {
  origin: 'Origin',
  ascent: 'Ascent',
  boost: 'Boost',
  mastery: 'Mastery',
  common_core: 'Common Core',
  common_boost: 'Common Boost',
};

export interface HexaTypeChip {
  nodeType: string;
  label: string;
}

/**
 * One summary chip per node type present in `nodes`, in `NODE_TYPE_ORDER` order — used by
 * `HexaOverview`'s per-character rows. A type with zero nodes present (a flexible type the player
 * hasn't added yet) is omitted entirely rather than rendered as an empty/zero chip.
 *
 * REV22 Fix 2 (docs/specs/REV22-hexa-stat-and-merged-masteries.md): `statNodes`, when given, appends
 * one more chip for the separate HEXA Stat matrix ("HEXA Stat n/3 at goal") — goal-based the same
 * way the fixed multi-node type chips are (a node's own `maxed`, not a hardcoded max level). Kept
 * out of `NODE_TYPE_ORDER`/`hexaRollup` entirely since HEXA Stat's fragment-RNG cost is deliberately
 * untracked — see `characterProgress.ts`'s `HexaStatNodeDto`.
 */
export function hexaTypeChips(nodes: HexaNodeDto[], statNodes?: HexaStatNodeDto[]): HexaTypeChip[] {
  const byType = new Map<string, HexaNodeDto[]>();
  for (const node of nodes) {
    const group = byType.get(node.nodeType);
    if (group) {
      group.push(node);
    } else {
      byType.set(node.nodeType, [node]);
    }
  }

  const chips: HexaTypeChip[] = [];
  for (const nodeType of NODE_TYPE_ORDER) {
    const group = byType.get(nodeType);
    if (!group || group.length === 0) continue;
    const label = CHIP_TYPE_LABEL[nodeType] ?? nodeType;
    if (SINGLE_NODE_TYPES.has(nodeType)) {
      // REV21: prefer the resolved real skill name (e.g. "Spirit Calibur 20") over the generic
      // "Origin 20" — falls back to the generic label when unresolved (unverified/uncataloged class).
      const resolvedLabel = group[0]!.skillName ?? label;
      chips.push({ nodeType, label: `${resolvedLabel} ${group[0]!.level}` });
    } else if (FLEXIBLE_NODE_TYPES.has(nodeType)) {
      chips.push({ nodeType, label: `${label} ${group.length} node${group.length === 1 ? '' : 's'}` });
    } else {
      // `n.maxed` is goal-based (level >= that node's own targetLevel), not necessarily level 30.
      const goalReachedCount = group.filter((n) => n.maxed).length;
      chips.push({ nodeType, label: `${label} ${goalReachedCount}/${group.length} at goal` });
    }
  }
  if (statNodes && statNodes.length > 0) {
    const atGoal = statNodes.filter((n) => n.maxed).length;
    chips.push({ nodeType: 'hexa_stat', label: `HEXA Stat ${atGoal}/${statNodes.length} at goal` });
  }
  return chips;
}

/**
 * REV20 — Erda Link classes (`ClassDto.sixthJobSystem === 'ERDA_LINK'`) don't use the HEXA Matrix
 * at all, so `HexaOverview` badges them "not tracked" instead of showing a bar/chips, and excludes
 * them from the account totals strip. First UI consumer of `sixthJobSystem` (see
 * `docs/specs/REV20-hexa-page.md`). Currently dormant: Sia Astelle / Erel Light were seeded as
 * ERDA_LINK by mistake and corrected to HEXA on 2026-09-06 (research 11, Shine addendum); no
 * seeded class uses ERDA_LINK today, the path is kept for a future class that genuinely does.
 */
export function isErdaLinkClass(classSlug: string, classes: ClassDto[]): boolean {
  return classes.find((k) => k.slug === classSlug)?.sixthJobSystem === 'ERDA_LINK';
}
